import React, { useState, type JSX } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { RootState, useAppDispatch, useTypedSelector } from '@fiji/common/src/app/store';
import { CustomTransComponent, Hierarchy, renderGroupHierarchyIcons } from '../../components';
import {
    useCreateGroupMutation,
    useDeleteGroupMutation,
    useGetAllGroupsMutation,
    useLazyGetGroupDetailsQuery,
    useMoveExistingGroupsAndDevicesMutation,
} from '@fiji/common/src/features/group/groupApi';
import { useTheme } from '@mui/material/styles';
import { EmptyState } from '@brightlayer-ui/react-components';
import WorkspacesIcon from '@mui/icons-material/Workspaces';
import { Box, Button, Stack, Typography } from '@mui/material';
import Add from '@mui/icons-material/Add';
import NotificationsActive from '@mui/icons-material/NotificationsActive';
import NotificationsActiveOutlined from '@mui/icons-material/NotificationsActiveOutlined';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import {
    addGroupModal,
    selectRefetchGroupHierarchy,
    setRefetchGroupHierarchy,
} from '@fiji/common/src/features/group/groupSlice';
import { GroupHierarchySkeleton } from './GroupHierarchySkeleton';
import { selectCurrentPermission } from '@fiji/common/src/features/profile/profileSlice';
import { useNewCustomTranslations, useRBAC } from '../../hooks';
import * as Colors from '@brightlayer-ui/colors';
import { useSwitchOrgMutation } from '@fiji/common/src/features/orgManagement/orgApi';
import { useDeleteDeviceMutation } from '@fiji/common/src/features/deviceManagement/deviceApi';
import { DEFAULT_HIERARCHY_SIZE } from '@fiji/common';
import { Device } from '@brightlayer-ui/icons-mui';
import { setAppHeaderTitle, setDrawerHeaderTranslationKey } from '@fiji/common/src/features/common/commonSlice';
import { useTranslation } from 'react-i18next';
import { HIERARCHICAL_SORT_ORDER } from '@fiji/common/src/constants';

const buildEmptyStateButtonSx = (theme: any): any => {
    const primary = theme?.palette?.primary;
    return {
        border: `1px solid ${primary?.main}`,
        '&:hover': {
            backgroundColor: primary?.[50],
        },
    };
};

const shouldSkipFetchHierarchy = (
    forceRefresh: boolean,
    paginationPayload: any,
    hierarchyMappingData: any,
    nodeId: any
): boolean => !forceRefresh && !paginationPayload?.page && Boolean(hierarchyMappingData?.[nodeId]);

const resolvePrevMappedRecords = (prev: any, nodeId: any, paginationPayload: any, forceRefresh: boolean): any[] => {
    if (!paginationPayload?.page || forceRefresh) return [];
    return prev?.[nodeId]?.records ?? [];
};

const buildGroupsDataUpdate = (prev: any, paginationPayload: any, childHierarchyData: any): any => {
    const childData = childHierarchyData?.data;
    const isPagination = Boolean(paginationPayload?.page);
    const prevRecords = isPagination ? prev?.records ?? [] : [];
    const childRecords = childData?.records ?? [];
    return {
        records: [...prevRecords, ...childRecords],
        total: childData?.total,
    };
};

const buildHierarchyMappingUpdate = (
    prev: any,
    currentNode: any,
    paginationPayload: any,
    childHierarchyData: any,
    forceRefresh: boolean
): any => {
    const nodeId = currentNode?.id;
    const childData = childHierarchyData?.data;
    const prevRecords = resolvePrevMappedRecords(prev, nodeId, paginationPayload, forceRefresh);
    const childRecords = childData?.records ?? [];
    return {
        ...prev,
        [nodeId]: {
            records: [...prevRecords, ...childRecords],
            total: childData?.total,
        },
    };
};

const findNodePath = (rootRecords: any, hierarchyMappingData: any, nodeId?: string): string[] | undefined => {
    if (!Array.isArray(rootRecords) || !nodeId) return undefined;

    const walk = (nodes: any[], path: string[]): string[] | undefined => {
        for (const node of nodes) {
            const nextPath = [...path, node?.id];
            if (node?.id === nodeId) return nextPath;
            const children = hierarchyMappingData?.[node?.id]?.records;
            const childPath = Array.isArray(children) ? walk(children, nextPath) : undefined;
            if (childPath) return childPath;
        }
        return undefined;
    };

    return walk(rootRecords, []);
};

const getSelectedNodeGatewayParentId = (node: any): string | undefined =>
    node?.parentDeviceId ??
    node?.parent?.deviceId ??
    (node?.wrapperDeviceType === 'Child' ? node?.parentId : undefined);

const getSelectedNodeGroupParentId = (node: any, gatewayParentId?: string): string | undefined =>
    gatewayParentId ? node?.groupId : node?.groupId ?? node?.parentId;

// Finds the single scrollable ancestor for the hierarchy list itself — native
// scrollIntoView() adjusts every scrollable ancestor it finds, which for a TALL expanded node
// (its own children rendered inside the same <li>) can scroll an outer container (e.g. a
// sidebar wrapper) instead, leaving only an ancestor row visible. Scrolling just this one
// container avoids that. Returns undefined if the walk reaches <body> without finding one.
const findScrollableHierarchyContainer = (node: HTMLElement): HTMLElement | undefined => {
    let container = node.parentElement;
    while (container && container !== document.body) {
        const style = window.getComputedStyle(container);
        if (container.scrollHeight > container.clientHeight && ['auto', 'scroll'].includes(style.overflowY)) {
            return container;
        }
        container = container.parentElement;
    }
    return undefined;
};

// Measures the node's own row (not the <li> subtree, which includes expanded children) and
// clamps the target scrollTop so the row lands as close to 2nd-from-top as the list allows.
const computeHierarchyScrollTarget = (container: HTMLElement, node: HTMLElement): number => {
    const rowHeight = node.firstElementChild?.getBoundingClientRect().height || 54;
    const targetTop =
        container.scrollTop + node.getBoundingClientRect().top - container.getBoundingClientRect().top - rowHeight;
    const maxScrollTop = container.scrollHeight - container.clientHeight;
    return Math.min(Math.max(0, targetTop), maxScrollTop);
};

const scrollSelectedNodeIntoView = (nodeId?: string): void => {
    if (!nodeId) return;
    const node = document.querySelector<HTMLElement>(`[data-testid="hierarchy-node-${nodeId}"]`);
    if (!node) return;

    const container = findScrollableHierarchyContainer(node);
    if (!container) return;

    container.scrollTo({ top: computeHierarchyScrollTarget(container, node), behavior: 'smooth' });
};

const getFirstRenderedHierarchyNodeId = (candidateIds: Array<string | undefined>): string | undefined => {
    for (const id of candidateIds) {
        if (!id) continue;
        if (document.querySelector(`[data-testid="hierarchy-node-${id}"]`)) return id;
    }
    return undefined;
};

const areStringArraysEqual = (a: string[], b: string[]): boolean =>
    a.length === b.length && a.every((value, index) => value === b[index]);

type HierarchyPageBucket = {
    records?: Array<{ id?: string }>;
    total?: number;
};

type HierarchyPageRequest = {
    containerId?: string;
    page: number;
};

const findNextAncestorPageRequest = (
    ancestorIds: string[],
    groupsData: HierarchyPageBucket | undefined,
    hierarchyMappingData: Record<string, HierarchyPageBucket>
): HierarchyPageRequest | undefined => {
    for (let level = 0; level < ancestorIds.length; level += 1) {
        const containerId = level === 0 ? undefined : ancestorIds[level - 1];
        const bucket = containerId ? hierarchyMappingData[containerId] : groupsData;
        const records = bucket?.records;
        const ancestorId = ancestorIds[level];
        if (Array.isArray(records) && records.some((record) => record.id === ancestorId)) continue;

        const loadedCount = records?.length ?? 0;
        const total = bucket?.total ?? 0;
        if (!loadedCount || loadedCount >= total) return undefined;

        return {
            containerId,
            page: Math.floor(loadedCount / DEFAULT_HIERARCHY_SIZE),
        };
    }
    return undefined;
};

export const GroupHierarchy = ({ handleHideDrawer }: { handleHideDrawer: any }): JSX.Element => {
    const theme: any = useTheme();
    const location = useLocation();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const selectedOrg = useTypedSelector((rootstate) => rootstate.org.selectedOrg);
    const currentRealmName = useTypedSelector((rootstate) => rootstate.common.selectedRealm);
    const hierarchyRefresh = useTypedSelector(selectRefetchGroupHierarchy);
    const sortOption = useTypedSelector((rootstate) => rootstate.common.sortOption);
    const [getFirstLevelGroup]: any = useGetAllGroupsMutation();
    const [getGroupChildren]: any = useGetAllGroupsMutation();
    const { t } = useTranslation();
    const { groupId } = useParams();
    const [sortOrder, setSortOrder] = React.useState<string>(sortOption === HIERARCHICAL_SORT_ORDER ? '' : sortOption);
    useNewCustomTranslations('GROUP_MANAGEMENT');
    useNewCustomTranslations('COMMON');

    const [, { isSuccess: createSuccess }] = useCreateGroupMutation({
        fixedCacheKey: 'createGroup',
    });
    const [, { isSuccess: deletionSuccess }] = useDeleteGroupMutation({
        fixedCacheKey: 'deleteGroup',
    });
    const [, { isSuccess: deviceDeletionSuccess }] = useDeleteDeviceMutation({
        fixedCacheKey: 'deleteDevice',
    });
    const [, { isSuccess: moveSuccess }] = useMoveExistingGroupsAndDevicesMutation({
        fixedCacheKey: 'moveData',
    });
    const permissions = useTypedSelector(selectCurrentPermission);
    const { hasPermission } = useRBAC(permissions);
    const canCreateGroup = hasPermission('group-management_create-groups');
    const canCreateDevice = hasPermission('create-devices');
    const selectedNode = useTypedSelector((rootstate: RootState) => rootstate?.common?.selectedNode);
    const [, { isSuccess: switchOrgSuccess }] = useSwitchOrgMutation({ fixedCacheKey: 'switchOrg' });
    const [groupsData, setGroupsData] = useState<any>();
    const [hierarchyMappingData, setHierarchyMappingData] = useState<any>({});
    const [loadingHierarchyNode, setLoadingHierarchyNode] = useState([]);
    const [controlledExpandedNodes, setControlledExpandedNodes] = useState<string[]>([]);
    const [triggerGetGroupDetails] = useLazyGetGroupDetailsQuery();
    const restoreRequestRef = React.useRef(1);
    const restoreNodeIdRef = React.useRef<string | undefined>(selectedNode?.id);
    const skipNextRestoreRef = React.useRef(false);
    const skipInitialRestoreForTreeClickRef = React.useRef(false);
    const previousPathRef = React.useRef(location.pathname);
    const requestedHierarchyNodesRef = React.useRef<Set<string>>(new Set());
    // Tracks which selectedNode.id has already had its ancestor path applied to
    // controlledExpandedNodes, so later unrelated hierarchyMappingData updates (e.g. from
    // manually expanding a different node) don't re-run and overwrite that expansion.
    const appliedPathNodeIdRef = React.useRef<string | undefined>(undefined);
    // Tracks which "<containerId>-<page>" auto-load requests have already been fired while
    // restoring, so the selected entity's page-1+ position doesn't get requested repeatedly.
    const requestedAutoPageRef = React.useRef<Set<string>>(new Set());
    // Set whenever the user manually clicks an expand/collapse chevron (not our own ancestor-
    // path effects), so the pending restore-scroll below can back off instead of fighting them.
    const manualExpandInteractionRef = React.useRef(false);
    // On a full page reload, selectedNode starts undefined and only becomes available once
    // route data finishes loading — location.pathname never changes in that case, so the
    // pathname-restore effect below never fires. Arm the restore the first time
    // selectedNode.id appears after mount so the scroll-into-view effect still runs.
    const initialRestoreDoneRef = React.useRef(false);
    // Root-down chain of ancestor ids for the current selection, filled in once the async
    // ancestor-walk effect resolves it — used to auto-paginate intermediate ancestors that
    // themselves sit beyond page 0 of their own parent's children.
    const [ancestorChain, setAncestorChain] = useState<{ nodeId?: string; chain: string[] } | undefined>(undefined);
    // Incremented on every root-list fetch; a response only gets applied if no newer request
    // has been issued since (so a stale/out-of-order response — e.g. superseded by a sort
    // change — can't overwrite a more recent one, and correctly-resolved responses never get
    // silently dropped either).
    const groupsDataRequestIdRef = React.useRef(0);

    React.useEffect(() => {
        appliedPathNodeIdRef.current = undefined;
        requestedAutoPageRef.current = new Set();
        setAncestorChain(undefined);
    }, [selectedNode?.id]);

    React.useEffect(() => {
        if (initialRestoreDoneRef.current || !selectedNode?.id) return;
        initialRestoreDoneRef.current = true;
        if (skipInitialRestoreForTreeClickRef.current) {
            skipInitialRestoreForTreeClickRef.current = false;
            restoreNodeIdRef.current = undefined;
            return;
        }
        restoreRequestRef.current += 1;
        restoreNodeIdRef.current = selectedNode.id;
        manualExpandInteractionRef.current = false;
    }, [selectedNode?.id]);

    const fetchRootGroups = async (): Promise<void> => {
        const requestId = ++groupsDataRequestIdRef.current;
        const body =
            sortOrder !== HIERARCHICAL_SORT_ORDER
                ? { page: 0, size: DEFAULT_HIERARCHY_SIZE, sort: { key: 'name', sortType: sortOrder } }
                : { page: 0, size: DEFAULT_HIERARCHY_SIZE };
        const result: any = await getFirstLevelGroup({ body });
        if (requestId !== groupsDataRequestIdRef.current) return;
        setGroupsData(result?.data?.data);
    };

    const resetHandler = (): void => {
        void fetchRootGroups();
        setGroupsData(null);
        setHierarchyMappingData({});
        setControlledExpandedNodes([]);
        requestedHierarchyNodesRef.current = new Set();
        requestedAutoPageRef.current = new Set();
        setAncestorChain(undefined);
    };

    React.useEffect(() => {
        if (hierarchyRefresh?.refetch) {
            dispatch(setRefetchGroupHierarchy({}));
            resetHandler();
        }
    }, [hierarchyRefresh?.refetch]);

    React.useEffect(() => {
        if (createSuccess || deletionSuccess || moveSuccess || deviceDeletionSuccess) {
            resetHandler();
        }
    }, [createSuccess, deletionSuccess, moveSuccess, deviceDeletionSuccess]);

    React.useEffect(() => {
        if (selectedOrg?.id) {
            void fetchRootGroups();
        }
        if (switchOrgSuccess) {
            setGroupsData(null);
        }
    }, [selectedOrg?.id, switchOrgSuccess]);

    React.useEffect(() => {
        if (!groupsData?.records?.length && !canCreateGroup) {
            dispatch(setAppHeaderTitle(t('COMMON:ALL_DEVICES')));
            dispatch(setDrawerHeaderTranslationKey('COMMON:ALL_DEVICES'));
        } else {
            dispatch(setAppHeaderTitle(selectedNode?.name));
            dispatch(setDrawerHeaderTranslationKey('COMMON:ALL_GROUPS'));
        }

        return () => {
            if (!window.location.pathname?.includes(`group/${groupId}`)) dispatch(setAppHeaderTitle(undefined));
        };
    }, [selectedNode, canCreateGroup, groupsData]);

    React.useEffect(() => {
        setSortOrder(sortOption);
    }, [sortOption]);

    React.useEffect(() => {
        if (previousPathRef.current === location.pathname) return;
        previousPathRef.current = location.pathname;
        if (skipNextRestoreRef.current) {
            skipNextRestoreRef.current = false;
            return;
        }
        restoreRequestRef.current += 1;
        restoreNodeIdRef.current = selectedNode?.id;
        manualExpandInteractionRef.current = false;
    }, [location.pathname]);

    React.useEffect(() => {
        const fetchHierarchy = async (currentNode: any, paginationPayload?: any): Promise<void> => {
            try {
                // Pass true as the third argument to force an immediate network reload
                await handleFetchHierarchy(currentNode, paginationPayload, true);
            } catch (error) {
                console.error('Error fetching hierarchy:', error);
            }
        };

        void fetchRootGroups();

        if (controlledExpandedNodes.length > 0) {
            controlledExpandedNodes.forEach((expandedNodeId) => {
                void fetchHierarchy({ id: expandedNodeId }, { page: 0, size: DEFAULT_HIERARCHY_SIZE });
            });
        }
    }, [sortOrder]);
    const handleFetchHierarchy = async (
        currentNode: any,
        paginationPayload?: any,
        forceRefresh = false
    ): Promise<void> => {
        const nodeId = currentNode?.id;
        // Modified the guard condition: If forceRefresh is TRUE, skip this return completely
        if (shouldSkipFetchHierarchy(forceRefresh, paginationPayload, hierarchyMappingData, nodeId)) {
            return;
        }

        setLoadingHierarchyNode((prevState): any => [
            ...prevState,
            paginationPayload?.page ? `more-button-${nodeId}` : nodeId,
        ]);

        let queryBody = { ...paginationPayload, sort: { key: 'name', sortType: sortOrder } };
        if (sortOrder === HIERARCHICAL_SORT_ORDER) {
            queryBody = { ...paginationPayload };
        }

        const { data: childHierarchyData }: any = await getGroupChildren({
            parent: nodeId,
            body: queryBody,
        });

        if (childHierarchyData?.success) {
            if (nodeId === undefined) {
                setGroupsData((prev: any) => buildGroupsDataUpdate(prev, paginationPayload, childHierarchyData));
            } else {
                setHierarchyMappingData((prev: any) =>
                    buildHierarchyMappingUpdate(prev, currentNode, paginationPayload, childHierarchyData, forceRefresh)
                );
            }
        }

        setLoadingHierarchyNode((prevState): any =>
            prevState.filter(
                (loadingNode) => loadingNode !== (paginationPayload?.page ? `more-button-${nodeId}` : nodeId)
            )
        );
    };

    React.useEffect(() => {
        if (!selectedOrg?.id || !selectedNode?.id || appliedPathNodeIdRef.current === selectedNode.id) return;
        const loadedPath = findNodePath(groupsData?.records, hierarchyMappingData, selectedNode.id);
        if (loadedPath?.length) {
            const ancestorPath = selectedNode.type === 'GROUP' ? loadedPath : loadedPath.slice(0, -1);
            appliedPathNodeIdRef.current = selectedNode.id;
            // Only replace the whole expanded set on a genuine restore (fresh mount, hamburger
            // reopen, navigate-away-and-back); an in-session selection merges its ancestor path
            // in instead, so previously expanded branches elsewhere in the tree stay open.
            const isRestoring = restoreNodeIdRef.current === selectedNode.id;
            setControlledExpandedNodes((prevNodes) => {
                const nextExpandedNodes = isRestoring
                    ? ancestorPath
                    : Array.from(new Set([...prevNodes, ...ancestorPath]));
                return areStringArraysEqual(prevNodes, nextExpandedNodes) ? prevNodes : nextExpandedNodes;
            });
        }
    }, [selectedOrg?.id, selectedNode?.id, selectedNode?.type, groupsData, hierarchyMappingData]);

    // Deps intentionally exclude groupsData/hierarchyMappingData: they change while this
    // async ancestor walk is in-flight and would cancel it before reaching the root.
    React.useEffect(() => {
        if (!selectedOrg?.id || !selectedNode?.id || appliedPathNodeIdRef.current === selectedNode.id) return;
        const loadedPath = findNodePath(groupsData?.records, hierarchyMappingData, selectedNode.id);
        if (loadedPath?.length) return;

        let cancelled = false;
        void (async (): Promise<void> => {
            const ancestors: string[] = [];
            // Devices/gateways fetched directly (e.g. dm/v1/devices/:id) don't carry a `type`
            // field, only `wrapperDeviceType`. Child devices under gateways use parentDeviceId
            // for their rendered parent, but their group ancestry still starts from groupId.
            const gatewayParentId = getSelectedNodeGatewayParentId(selectedNode);
            let parentId = getSelectedNodeGroupParentId(selectedNode, gatewayParentId);
            while (parentId && parentId !== selectedOrg.id && !ancestors.includes(parentId)) {
                ancestors.unshift(parentId);
                // Each request determines the next parentId, so this traversal must remain sequential.
                // eslint-disable-next-line no-await-in-loop
                const { data: parent }: any = await triggerGetGroupDetails(parentId);
                parentId = parent?.data?.parentId;
            }
            if (gatewayParentId && !ancestors.includes(gatewayParentId)) {
                ancestors.push(gatewayParentId);
            }
            if (!cancelled && appliedPathNodeIdRef.current !== selectedNode.id) {
                setAncestorChain({ nodeId: selectedNode.id, chain: ancestors });
                const ancestorPath = selectedNode.type === 'GROUP' ? [...ancestors, selectedNode.id] : ancestors;
                appliedPathNodeIdRef.current = selectedNode.id;
                // Same restore-vs-in-session distinction as the fast-path effect above.
                const isRestoring = restoreNodeIdRef.current === selectedNode.id;
                setControlledExpandedNodes((prevNodes) => {
                    const nextExpandedNodes = isRestoring
                        ? ancestorPath
                        : Array.from(new Set([...prevNodes, ...ancestorPath]));
                    return areStringArraysEqual(prevNodes, nextExpandedNodes) ? prevNodes : nextExpandedNodes;
                });
            }
        })();
        return (): void => {
            cancelled = true;
        };
    }, [
        selectedOrg?.id,
        selectedNode?.id,
        selectedNode?.type,
        selectedNode?.parentId,
        selectedNode?.parentDeviceId,
        selectedNode?.parent?.deviceId,
        selectedNode?.groupId,
        selectedNode?.wrapperDeviceType,
    ]);

    React.useEffect(() => {
        controlledExpandedNodes.forEach((nodeId) => {
            if (hierarchyMappingData[nodeId] || requestedHierarchyNodesRef.current.has(nodeId)) return;
            requestedHierarchyNodesRef.current.add(nodeId);
            void handleFetchHierarchy({ id: nodeId }, { page: 0, size: DEFAULT_HIERARCHY_SIZE });
        });
    }, [controlledExpandedNodes, hierarchyMappingData]);

    // The selected entity may sit beyond page 0 of its parent's children (only loaded via
    // manual "Show More" clicks otherwise), in which case it never renders and the restore
    // effect below keeps landing on the nearest rendered ancestor. Auto-fetch further pages
    // of its bucket, one page at a time, until it's loaded or the bucket is exhausted.
    React.useEffect(() => {
        if (!selectedOrg?.id || !selectedNode?.id || restoreNodeIdRef.current !== selectedNode.id) return;

        const parentId = getSelectedNodeGatewayParentId(selectedNode) ?? selectedNode.groupId ?? selectedNode.parentId;
        const containerId = parentId && parentId !== selectedOrg.id ? parentId : undefined;
        const bucket = containerId ? hierarchyMappingData[containerId] : groupsData;
        const records = bucket?.records;
        if (!Array.isArray(records) || records.some((record: any) => record?.id === selectedNode.id)) return;

        const loadedCount = records.length;
        const total = bucket?.total ?? 0;
        if (!loadedCount || loadedCount >= total) return;

        const nextPage = Math.floor(loadedCount / DEFAULT_HIERARCHY_SIZE);
        const requestKey = `${containerId ?? 'root'}-${nextPage}`;
        if (requestedAutoPageRef.current.has(requestKey)) return;

        requestedAutoPageRef.current.add(requestKey);
        void handleFetchHierarchy(containerId ? { id: containerId } : {}, {
            page: nextPage,
            size: DEFAULT_HIERARCHY_SIZE,
        });
    }, [
        selectedOrg?.id,
        selectedNode?.id,
        selectedNode?.groupId,
        selectedNode?.parentId,
        selectedNode?.parentDeviceId,
        selectedNode?.parent?.deviceId,
        selectedNode?.wrapperDeviceType,
        groupsData,
        hierarchyMappingData,
    ]);

    // For a multi-level selection, an INTERMEDIATE ancestor can itself sit beyond page 0 of ITS
    // own parent's children — its <li> never renders, so nothing nested inside it renders either,
    // regardless of controlledExpandedNodes. Walk the resolved ancestor chain root-down and
    // auto-fetch the next page of whichever level is the first one missing from its container.
    React.useEffect(() => {
        if (!selectedOrg?.id || !selectedNode?.id || restoreNodeIdRef.current !== selectedNode.id) return;
        if (!ancestorChain || ancestorChain.nodeId !== selectedNode.id) return;

        const nextPageRequest = findNextAncestorPageRequest(ancestorChain.chain, groupsData, hierarchyMappingData);
        if (!nextPageRequest) return;

        const requestKey = `${nextPageRequest.containerId ?? 'root'}-${nextPageRequest.page}`;
        if (requestedAutoPageRef.current.has(requestKey)) return;

        requestedAutoPageRef.current.add(requestKey);
        void handleFetchHierarchy(nextPageRequest.containerId ? { id: nextPageRequest.containerId } : {}, {
            page: nextPageRequest.page,
            size: DEFAULT_HIERARCHY_SIZE,
        });
    }, [selectedOrg?.id, selectedNode?.id, ancestorChain, groupsData, hierarchyMappingData]);

    React.useEffect(() => {
        if (!selectedOrg?.id || restoreNodeIdRef.current !== selectedNode?.id) return;
        const frame = window.requestAnimationFrame(() => {
            if (!restoreRequestRef.current) return;

            // A manual expand/collapse click means the user is actively driving the view —
            // stop the pending auto-scroll instead of pulling them back to the selection.
            if (manualExpandInteractionRef.current) {
                manualExpandInteractionRef.current = false;
                restoreNodeIdRef.current = undefined;
                return;
            }

            // Wait until the selected node itself is rendered — deeper levels appear
            // progressively as their hierarchy data loads, and each one shifts the layout.
            // No fallback scroll to an ancestor here: the selected node itself must land 2nd
            // from top, so we simply wait (re-runs as more data loads) instead of settling.
            if (!getFirstRenderedHierarchyNodeId([selectedNode.id])) return;

            scrollSelectedNodeIntoView(selectedNode.id);
            // If another branch (or the selected group's own children) is still loading,
            // its rows can still be inserted above this one and push it back out of view —
            // keep re-scrolling on each subsequent load until nothing is pending, only then
            // consume the restore so later manual expand/collapse clicks don't re-trigger it.
            if (!loadingHierarchyNode.length) {
                restoreNodeIdRef.current = undefined;
            }
        });
        return () => window.cancelAnimationFrame(frame);
    }, [
        selectedOrg?.id,
        selectedNode?.id,
        controlledExpandedNodes,
        groupsData,
        hierarchyMappingData,
        loadingHierarchyNode,
        ancestorChain,
    ]);

    // Safety net for nested hierarchy items: each ancestor level resolves via its own async
    // fetch, so the effect above can settle between two levels arriving (e.g. batched state
    // updates that don't change groupsData/hierarchyMappingData refs together). Retry a few
    // times on a timer, independent of those deps, until the restore is actually consumed.
    React.useEffect(() => {
        if (!selectedOrg?.id || restoreNodeIdRef.current !== selectedNode?.id) return;
        const retryDelaysMs = [150, 400, 800, 1500, 2500, 3000, 5000];
        const timers = retryDelaysMs.map((delay) =>
            window.setTimeout(() => {
                if (restoreNodeIdRef.current !== selectedNode?.id || manualExpandInteractionRef.current) return;
                if (getFirstRenderedHierarchyNodeId([selectedNode.id])) {
                    scrollSelectedNodeIntoView(selectedNode.id);
                }
            }, delay)
        );
        return () => timers.forEach((timer) => window.clearTimeout(timer));
    }, [selectedOrg?.id, selectedNode?.id]);

    // const handleCleanHierarchyNode = (parentId: string): void => {
    //     setHierarchyMappingData((prevState: any) => {
    //         const newHierarchyMappingData: any = { ...prevState };
    //         delete newHierarchyMappingData[parentId];
    //         return newHierarchyMappingData;
    //     });
    // };

    const handleCleanHierarchyNode = (parentId: string): void => {
        setHierarchyMappingData((prevState: any) => {
            // Do not delete data to preserve pagination and show more state
            if (!prevState[parentId]) return prevState;

            return {
                ...prevState,
                [parentId]: prevState[parentId],
            };
        });
    };

    const handleNodeClick = (selectedNodeData: any): void => {
        restoreNodeIdRef.current = undefined;
        skipInitialRestoreForTreeClickRef.current = true;
        skipNextRestoreRef.current = true;
        if (selectedNodeData?.type === 'GROUP') {
            navigate(`/${currentRealmName}/group/${selectedNodeData.id}`);
        } else {
            navigate(`/${currentRealmName}/device/${selectedNodeData.id}`);
        }
    };

    const handleOpenAddGroupModal = (group?: any): any => dispatch(addGroupModal({ isOpen: true, group }));

    const hasChildNode = (treeItem: any): boolean =>
        treeItem?.groupCount || treeItem?.deviceCount || hierarchyMappingData[treeItem?.id]?.length;

    const getActions = ({ treeItem }: any): JSX.Element => {
        const item = treeItem ?? {};
        const { alarmCount, type, warningCount } = item;
        if (!alarmCount) return <></>;
        const total = alarmCount + (warningCount ?? 0);
        if (type === 'GROUP') {
            return (
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <NotificationsActiveOutlined sx={{ color: Colors.gray[500], marginRight: 1 }} />
                    <Typography sx={{ paddingRight: 1, fontWeight: '600', fontSize: 16 }}>{total}</Typography>
                </Box>
            );
        }
        if (type === 'DEVICE' || type === 'GATEWAY') {
            return (
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <NotificationsActive sx={{ color: Colors.red[500], marginRight: 1 }} />
                    <Typography sx={{ paddingRight: 1, fontWeight: '600', fontSize: 16, color: Colors.red[500] }}>
                        {total}
                    </Typography>
                </Box>
            );
        }
        return <></>;
    };

    const getEmptyStateButton = (): JSX.Element => {
        if (canCreateGroup) {
            return (
                <Button
                    id="add-group"
                    sx={buildEmptyStateButtonSx(theme)}
                    variant={'outlined'}
                    color={'primary'}
                    startIcon={<Add />}
                    onClick={(): void => handleOpenAddGroupModal()}
                >
                    <CustomTransComponent translationKey={'GROUP_MANAGEMENT:BTN.ADD_A_GROUP'} />
                </Button>
            );
        } else if (canCreateDevice) {
            return (
                <Button
                    id="add-group"
                    sx={buildEmptyStateButtonSx(theme)}
                    variant={'contained'}
                    color={'primary'}
                    startIcon={<Add data-testid="AddDeviceIcon" />}
                    onClick={(): void => navigate(`/${currentRealmName}/add-device`)}
                >
                    <CustomTransComponent translationKey={'COMMON:ADD_DEVICE'} />
                </Button>
            );
        }
        return <></>;
    };

    return (
        <Hierarchy
            labelKey={'name'}
            data={groupsData}
            loadMore={handleFetchHierarchy}
            cleanHiearchyNode={handleCleanHierarchyNode}
            hierarchyMappingData={hierarchyMappingData}
            loadingNode={loadingHierarchyNode}
            labelColor={'error'}
            handleTreeNodeSelection={handleNodeClick}
            controlledExpandedNodes={controlledExpandedNodes}
            setControlledExpandedNodes={(updater: any): void => {
                manualExpandInteractionRef.current = true;
                setControlledExpandedNodes(updater);
            }}
            selectionType={'node'} // radio, checkbox, node
            selectedNodes={selectedNode?.id}
            isLoading={!groupsData}
            noDataFoundComponent={
                <Stack className="center w-100">
                    <EmptyState
                        icon={canCreateGroup ? <WorkspacesIcon fontSize={'inherit'} /> : <Device fontSize="inherit" />}
                        title={canCreateGroup ? t('GROUP_MANAGEMENT:LABELS.NO_GROUPS') : t('COMMON:NO_DEVICES')}
                        {...(canCreateGroup && {
                            description: t('COMMON:SELECT_GROUP_NO_DESC'),
                        })}
                        actions={
                            <Stack spacing={2}>
                                {getEmptyStateButton()}
                                <Button
                                    id="hide-panel"
                                    sx={buildEmptyStateButtonSx(theme)}
                                    variant={'outlined'}
                                    color={'primary'}
                                    startIcon={<VisibilityOffIcon />}
                                    onClick={handleHideDrawer}
                                >
                                    <CustomTransComponent translationKey={'GROUP_HIERARCHY:HIDE_THIS_PANEL'} />
                                </Button>
                            </Stack>
                        }
                    />
                </Stack>
            }
            loadingSkeleton={<GroupHierarchySkeleton row={1} />}
            hasChild={hasChildNode}
            getAvatarChild={renderGroupHierarchyIcons}
            pagination={{ records: 'records', total: 'total', size: DEFAULT_HIERARCHY_SIZE }}
            actions={getActions}
        />
    );
};
